import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { tokens } from '@/theme'
import {
  archiveAnnouncement,
  createAnnouncement,
  listAdminAnnouncements,
  publishAnnouncement,
  TenantAdminApiError,
  updateAnnouncement,
  type AdminAnnouncement,
} from '@/lib/tenantAdminApi'
import { formatDay } from '@/lib/dateOnly'
import { StatusChip } from '@/pages/tenant-admin/TenantAdminDashboard'
import { useTenantAdmin } from '@/pages/tenant-admin/context'

/**
 * Screen 4 — announcements: simple list + compose (ag-008 wireframes).
 *
 * GSA-authored announcements publish directly (the GSA is the authority for
 * its own content). Delete is a soft archive — recoverable via re-publish, no
 * destructive hard-delete in the MVP.
 */
export default function TenantAdminAnnouncements() {
  const { slug, displayName } = useTenantAdmin()
  const navigate = useNavigate()

  const [items, setItems] = useState<AdminAnnouncement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Compose form (also used for editing an existing announcement).
  const [editingId, setEditingId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const bail = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof TenantAdminApiError && err.status === 401) {
        navigate(`/g/${slug}/admin/login`, { replace: true })
        return
      }
      setError(err instanceof Error ? err.message : fallback)
    },
    [navigate, slug],
  )

  const reload = useCallback(() => {
    return listAdminAnnouncements(slug)
      .then(setItems)
      .catch((err) => bail(err, 'Failed to load announcements.'))
  }, [slug, bail])

  useEffect(() => {
    setLoading(true)
    reload().finally(() => setLoading(false))
  }, [reload])

  const resetForm = () => {
    setEditingId(null)
    setTitle('')
    setBody('')
  }

  const doSubmit = (publish: boolean) => {
    if (submitting || !title.trim()) return
    setSubmitting(true)
    setError(null)

    const op =
      editingId === null
        ? createAnnouncement(slug, { title: title.trim(), body, publish })
        : updateAnnouncement(slug, editingId, { title: title.trim(), body }).then((updated) =>
            publish && updated.status !== 'published'
              ? publishAnnouncement(slug, editingId)
              : updated,
          )

    op.then(() => {
      resetForm()
      return reload()
    })
      .catch((err) => bail(err, 'Failed to save the announcement.'))
      .finally(() => setSubmitting(false))
  }

  const doAction = (action: 'publish' | 'archive', id: number) => {
    setError(null)
    const call = action === 'publish' ? publishAnnouncement : archiveAnnouncement
    call(slug, id)
      .then(() => reload())
      .catch((err) => bail(err, `Failed to ${action} the announcement.`))
  }

  const startEdit = (a: AdminAnnouncement) => {
    setEditingId(a.id)
    setTitle(a.title)
    setBody(a.body)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Announcements
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Published announcements appear on your public page immediately, badged “Posted by{' '}
        {displayName}”. Archiving removes one from the page (recoverable).
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* ── Compose / edit ──────────────────────────────────────────────── */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingId === null ? 'New announcement' : 'Edit announcement'}
          </Typography>
          <form
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault()
              doSubmit(true)
            }}
          >
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                helperText="Plain text or simple markdown."
              />
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Button type="submit" variant="contained" disabled={submitting || !title.trim()}>
                  {editingId === null ? 'Publish' : 'Save & publish'}
                </Button>
                <Button
                  variant="outlined"
                  disabled={submitting || !title.trim()}
                  onClick={() => doSubmit(false)}
                >
                  {editingId === null ? 'Save draft' : 'Save changes'}
                </Button>
                {editingId !== null ? (
                  <Button onClick={resetForm} disabled={submitting}>
                    Cancel edit
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* ── List ────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={30} sx={{ color: tokens.green.main }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No announcements yet — your first one will appear here.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {items.map((a) => (
            <Card key={a.id}>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.05rem', minWidth: 0 }} noWrap>
                    {a.title}
                  </Typography>
                  <StatusChip status={a.status} />
                </Stack>
                {a.body ? (
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', whiteSpace: 'pre-line', mb: 1.5 }}
                  >
                    {a.body}
                  </Typography>
                ) : null}
                <Divider sx={{ mb: 1.5 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', mr: 'auto' }}>
                    {a.status === 'published' && a.published_at
                      ? `Published ${formatDay(a.published_at)}`
                      : a.updated_at
                        ? `Updated ${formatDay(a.updated_at)}`
                        : ''}
                  </Typography>
                  <Button size="small" onClick={() => startEdit(a)}>
                    Edit
                  </Button>
                  {a.status !== 'published' ? (
                    <Button size="small" variant="outlined" onClick={() => doAction('publish', a.id)}>
                      {a.status === 'archived' ? 'Restore & publish' : 'Publish'}
                    </Button>
                  ) : null}
                  {a.status !== 'archived' ? (
                    <Button size="small" color="error" onClick={() => doAction('archive', a.id)}>
                      Archive
                    </Button>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}
